/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/
\* =========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListSubheader from '@mui/material/ListSubheader';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const ROW_H = 40;
const LIST_H = 320;

const initFind = {
  page: 1,
  perPage: 10,
  warehouse: [],
  product_ids: [],
  cat_ids: [],
  remark: [],
  date_start: Func.dateFirstMonth(),
  date_stop: Func.dateEndMonth(),
};

const normalizeWarehouse = (values) => [
  ...new Set((Array.isArray(values) ? values : [values]).map(Number).filter((id) => id > 0)),
];

function RpAdjustStock() {
  const mod = 'rp_adjust_stock';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [cboWarehouse, setCboWarehouse] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [remarkList, setRemarkList] = useState([]);
  const [codeSearch, setCodeSearch] = useState('');
  const [remarkSearch, setRemarkSearch] = useState('');
  const [openProducts, setOpenProducts] = useState(false);
  const [openRemarks, setOpenRemarks] = useState(false);
  const [listScroll, setListScroll] = useState(0);
  const [remarkScroll, setRemarkScroll] = useState(0);
  const productAnchor = useRef(null);
  const remarkAnchor = useRef(null);
  const listRef = useRef(null);
  const remarkListRef = useRef(null);
  const [urlExcel, setUrlExcel] = useState('');
  const [showMoney, setShowMoney] = useState(true);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const signed = (value, digit) => {
    const num = Number(value) || 0;
    const text = digit == null ? Func.numberDisplay(num) : Func.numberDisplay(num, digit);
    return num > 0 ? `+${text}` : text;
  };

  const pickedIds = (ids, all) => {
    if (!ids.length || (all.length > 0 && ids.length === all.length)) return '';
    return ids.join(',');
  };

  const pickedRemarks = (selected) => {
    if (!selected?.length || (remarkList.length > 0 && selected.length === remarkList.length)) {
      return '';
    }
    return selected;
  };

  const choices = useMemo(() => {
    const query = codeSearch.trim().toLowerCase();
    if (!query) return products;
    return products.filter((item) => (item.code || '').toLowerCase().includes(query));
  }, [products, codeSearch]);

  const remarkChoices = useMemo(() => {
    const query = remarkSearch.trim().toLowerCase();
    if (!query) return remarkList;
    return remarkList.filter((item) => (item.name || '').toLowerCase().includes(query));
  }, [remarkList, remarkSearch]);

  const listStart = Math.max(0, Math.floor(listScroll / ROW_H) - 8);
  const listEnd = Math.min(choices.length, listStart + Math.ceil(LIST_H / ROW_H) + 16);
  const remarkStart = Math.max(0, Math.floor(remarkScroll / ROW_H) - 8);
  const remarkEnd = Math.min(remarkChoices.length, remarkStart + Math.ceil(LIST_H / ROW_H) + 16);

  const productLabel = () => {
    if (!find.product_ids.length || find.product_ids.length === products.length) return 'ทั้งหมด';
    return products
      .filter((item) => find.product_ids.some((row) => String(row) === String(item.id)))
      .map((item) => item.code)
      .join(', ');
  };

  const remarkLabel = () => {
    if (!find.remark.length || find.remark.length === remarkList.length) return 'ทั้งหมด';
    return remarkList
      .filter((item) => find.remark.some((row) => String(row) === String(item.id)))
      .map((item) => item.name)
      .join(', ');
  };

  function view(data) {
    const warehouse = normalizeWarehouse(data.warehouse);
    const payload = { ...data, warehouse };
    setFind(payload);
    closeShow();

    if (warehouse.length === 0) {
      setRecord([]);
      setPageTotal(0);
      setPageNo(1);
      setLoading(false);
      return;
    }

    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      page: payload.page,
      per_page: payload.perPage,
      warehouse: payload.warehouse,
      product_id: pickedIds(payload.product_ids || [], products),
      cat: pickedIds(payload.cat_ids || [], categories),
      remark: pickedRemarks(payload.remark),
      date_start: payload.date_start,
      date_stop: payload.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  }

  const toggleProduct = (id) => {
    const picked = find.product_ids.some((row) => String(row) === String(id));
    view({
      ...find,
      product_ids: picked
        ? find.product_ids.filter((row) => String(row) !== String(id))
        : [...find.product_ids, id],
      page: 1,
    });
  };

  const toggleRemark = (id) => {
    const picked = find.remark.some((row) => String(row) === String(id));
    view({
      ...find,
      remark: picked
        ? find.remark.filter((row) => String(row) !== String(id))
        : [...find.remark, id],
      page: 1,
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateFormat(start.toDate()),
      date_stop: Func.dateFormat(stop.toDate()),
      page: 1,
    });
  };

  const excel = () => {
    if (find.warehouse.length === 0) return;
    const params = new URLSearchParams();
    find.warehouse.forEach((id) => params.append('warehouse[]', id));
    params.append('product_id', pickedIds(find.product_ids, products));
    params.append('cat', pickedIds(find.cat_ids, categories));
    const remark = pickedRemarks(find.remark);
    if (Array.isArray(remark)) {
      remark.forEach((item) => params.append('remark[]', item));
    }
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);
    params.append('show_price', showMoney ? '1' : '0');
    params.append('show_amount', showMoney ? '1' : '0');
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        const warehouse = result.warehouse || [];
        setCboWarehouse(warehouse);
        setProducts(result.products || []);
        setCategories(result.categories || []);
        setRemarkList(result.remarks || []);
        setUrlExcel(result.excel);
        view({ ...find, warehouse: warehouse.map((item) => Number(item.id)) });
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <MDBox ref={productAnchor}>
              <MDInput
                fullWidth
                value={productLabel()}
                onClick={() => setOpenProducts(true)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position='start'>{t('attr.product_code')} :</InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <Menu
              anchorEl={productAnchor.current}
              open={openProducts}
              onClose={() => setOpenProducts(false)}
              disableAutoFocusItem
              PaperProps={{
                style: {
                  maxHeight: 480,
                  width: productAnchor.current ? productAnchor.current.clientWidth : undefined,
                },
              }}
            >
              <ListSubheader
                sx={{ bgcolor: 'background.paper' }}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <MDInput
                  autoFocus
                  fullWidth
                  placeholder='ค้นหารหัส'
                  value={codeSearch}
                  onChange={(e) => {
                    setCodeSearch(e.target.value);
                    setListScroll(0);
                    if (listRef.current) listRef.current.scrollTop = 0;
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              <MenuItem
                onClick={() => {
                  const allSelected =
                    products.length > 0 && find.product_ids.length === products.length;
                  view({
                    ...find,
                    product_ids: allSelected ? [] : products.map((item) => item.id),
                    page: 1,
                  });
                }}
              >
                <Checkbox
                  checked={products.length > 0 && find.product_ids.length === products.length}
                />
                ทั้งหมด
              </MenuItem>
              <div
                ref={listRef}
                style={{
                  height: Math.min(LIST_H, Math.max(choices.length, 1) * ROW_H),
                  overflowY: 'auto',
                }}
                onScroll={(e) => setListScroll(e.target.scrollTop)}
              >
                <div style={{ height: choices.length * ROW_H, position: 'relative' }}>
                  {choices.slice(listStart, listEnd).map((item, index) => (
                    <MenuItem
                      key={item.id}
                      style={{
                        position: 'absolute',
                        top: (listStart + index) * ROW_H,
                        height: ROW_H,
                        width: '100%',
                      }}
                      onClick={() => toggleProduct(item.id)}
                    >
                      <Checkbox
                        checked={find.product_ids.some((row) => String(row) === String(item.id))}
                      />
                      {item.code}
                    </MenuItem>
                  ))}
                </div>
              </div>
            </Menu>
          </Grid>
          <Grid item xs={12} md={4}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected.length || selected.length === categories.length) return 'ทั้งหมด';
                  return categories
                    .filter((item) => selected.includes(Number(item.id)))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.cat_ids}
              onChange={(e) => {
                const { value } = e.target;
                if (value.includes('ALL')) {
                  const allSelected =
                    categories.length > 0 && find.cat_ids.length === categories.length;
                  view({
                    ...find,
                    cat_ids: allSelected ? [] : categories.map((item) => Number(item.id)),
                    page: 1,
                  });
                } else {
                  view({
                    ...find,
                    cat_ids: normalizeWarehouse(value),
                    page: 1,
                  });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('attr.product_category')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox
                  checked={categories.length > 0 && find.cat_ids.length === categories.length}
                />
                ทั้งหมด
              </MenuItem>
              {categories.map((item) => (
                <MenuItem key={item.id} value={Number(item.id)}>
                  <Checkbox checked={find.cat_ids.includes(Number(item.id))} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} md={4}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboWarehouse.length) return 'ทั้งหมด';
                  return cboWarehouse
                    .filter((item) => selected.includes(Number(item.id)))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.warehouse}
              onChange={(e) => {
                const { value } = e.target;
                if (value.includes('ALL')) {
                  const allSelected = find.warehouse.length === cboWarehouse.length;
                  view({
                    ...find,
                    warehouse: allSelected ? [] : cboWarehouse.map((item) => Number(item.id)),
                    page: 1,
                  });
                } else {
                  view({ ...find, warehouse: normalizeWarehouse(value), page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.warehouse')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox
                  checked={find.warehouse.length === cboWarehouse.length && cboWarehouse.length > 0}
                />
                ทั้งหมด
              </MenuItem>
              {cboWarehouse.map((item) => (
                <MenuItem key={item.id} value={Number(item.id)}>
                  <Checkbox checked={find.warehouse.includes(Number(item.id))} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} md={4}>
            <MDBox ref={remarkAnchor}>
              <MDInput
                fullWidth
                value={remarkLabel()}
                onClick={() => setOpenRemarks(true)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position='start'>{t('attr.remark')} :</InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <Menu
              anchorEl={remarkAnchor.current}
              open={openRemarks}
              onClose={() => setOpenRemarks(false)}
              disableAutoFocusItem
              PaperProps={{
                style: {
                  maxHeight: 480,
                  width: remarkAnchor.current ? remarkAnchor.current.clientWidth : undefined,
                },
              }}
            >
              <ListSubheader
                sx={{ bgcolor: 'background.paper' }}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <MDInput
                  autoFocus
                  fullWidth
                  placeholder='ค้นหาหมายเหตุ'
                  value={remarkSearch}
                  onChange={(e) => {
                    setRemarkSearch(e.target.value);
                    setRemarkScroll(0);
                    if (remarkListRef.current) remarkListRef.current.scrollTop = 0;
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              <MenuItem
                onClick={() => {
                  const allSelected =
                    remarkList.length > 0 && find.remark.length === remarkList.length;
                  view({
                    ...find,
                    remark: allSelected ? [] : remarkList.map((item) => item.id),
                    page: 1,
                  });
                }}
              >
                <Checkbox
                  checked={remarkList.length > 0 && find.remark.length === remarkList.length}
                />
                ทั้งหมด
              </MenuItem>
              <div
                ref={remarkListRef}
                style={{
                  height: Math.min(LIST_H, Math.max(remarkChoices.length, 1) * ROW_H),
                  overflowY: 'auto',
                }}
                onScroll={(e) => setRemarkScroll(e.target.scrollTop)}
              >
                <div style={{ height: remarkChoices.length * ROW_H, position: 'relative' }}>
                  {remarkChoices.slice(remarkStart, remarkEnd).map((item, index) => (
                    <MenuItem
                      key={item.id}
                      style={{
                        position: 'absolute',
                        top: (remarkStart + index) * ROW_H,
                        height: ROW_H,
                        width: '100%',
                      }}
                      onClick={() => toggleRemark(item.id)}
                    >
                      <Checkbox
                        checked={find.remark.some((row) => String(row) === String(item.id))}
                      />
                      {item.name}
                    </MenuItem>
                  ))}
                </div>
              </div>
            </Menu>
          </Grid>
          <Grid item xs={12} md={4}>
            <MDDateRange
              dateOnly
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() =>
                view({
                  ...find,
                  date_start: Func.dateFirstMonth(),
                  date_stop: Func.dateEndMonth(),
                  page: 1,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MDBox
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              flexWrap='wrap'
              gap={1}
              height='100%'
            >
              <MDBox display='flex' alignItems='center' flexWrap='wrap'>
                <FormControlLabel
                  sx={{ mr: 1 }}
                  control={
                    <Checkbox
                      checked={showMoney}
                      onChange={(e) => setShowMoney(e.target.checked)}
                    />
                  }
                  label='ราคาต่อหน่วย / จำนวนเงิน'
                />
              </MDBox>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table' size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>ลำดับ</TableCell>
                    <TableCell>วันที่/รหัสสินค้า</TableCell>
                    <TableCell>รายละเอียด/หมายเหตุ</TableCell>
                    <TableCell>คลัง</TableCell>
                    <TableCell align='right'>จำนวน</TableCell>
                    <TableCell>หน่วยนับ</TableCell>
                    {showMoney ? <TableCell align='right'>ราคาต่อหน่วย</TableCell> : null}
                    {showMoney ? <TableCell align='right'>จำนวนเงิน</TableCell> : null}
                    <TableCell>ผู้ตรวจนับ</TableCell>
                    <TableCell>ชื่อผู้อนุมัติ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.no}</TableCell>
                      <TableCell>
                        <MDBox>{Func.dateDisplay(row.date_record)}</MDBox>
                        <MDBox>{row.code}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.name}</MDBox>
                        <MDBox>{row.remark || '-'}</MDBox>
                      </TableCell>
                      <TableCell>{row.warehouse_name}</TableCell>
                      <TableCell align='right'>{signed(row.quantity_diff)}</TableCell>
                      <TableCell>{row.unit_name}</TableCell>
                      {showMoney ? (
                        <TableCell align='right'>{Func.numberDisplay(row.cost_fix, 2)}</TableCell>
                      ) : null}
                      {showMoney ? (
                        <TableCell align='right'>{signed(row.amount, 2)}</TableCell>
                      ) : null}
                      <TableCell>{row.counter_name || '-'}</TableCell>
                      <TableCell>{row.audit_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                <Menu
                  anchorEl={show}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  open={Boolean(show)}
                  onClose={closeShow}
                  keepMounted
                >
                  <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
                    {t(`element.show_record`, { num: 10 })}
                  </MenuItem>
                  <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
                    {t(`element.show_record`, { num: 20 })}
                  </MenuItem>
                  <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
                    {t(`element.show_record`, { num: 100 })}
                  </MenuItem>
                </Menu>
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_account`), t(`menu.rp_adjust_stock`)]} />
      {renderList()}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

export default RpAdjustStock;
